import {
  BatchLoadDrop,
  BatchLoadExperimentsData,
  EValidationResult,
} from 'business/models/Models';
import BatchloadExperimentReviewExpOptions from 'enums/BatchloadExperimentReviewExpOptions';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { GetNormalizedBatchloadEntryValue, isNullOrUndefined } from 'utils/Global';
import BatchLoadExperimentNameReview from './BatchLoadExperimentNameReview';
import BatchLoadExperimentWizardContext from './BatchLoadExperimentWizardContext';
import PreviewBatchloadDataTable from './PreviewBatchloadDataTable';
import {
  formatDateWithParts,
  shortDayMonthYearFormat,
} from '../Hooks/ExperimentCommands/utils';
import { FmlxBanner, FmlxProgressBar } from 'fmlx-common-ui';
import {
  ESampleValidationStatusCode,
  getExperimentName,
  groupExperimentsByPlate,
  isSameDropIngredients,
} from './utils';

function getReviewExpEnumVal(reviewVal: string) {
  switch (reviewVal) {
    case '<PlateID':
      return BatchloadExperimentReviewExpOptions.PLATE_ID;
    case '<ScreenName':
      return BatchloadExperimentReviewExpOptions.SCREENNAME;
    case '<Temperature':
      return BatchloadExperimentReviewExpOptions.TEMPERATURE;
    case '<PlateBarcode':
      return BatchloadExperimentReviewExpOptions.PLATE_BARCODE;
    case '<Date':
      return BatchloadExperimentReviewExpOptions.DATE;
    case '<ExperimentNameTag':
      return BatchloadExperimentReviewExpOptions.EXP_NAME_TAG;

    default:
      return undefined;
  }
}

export default function PreviewDataBeforeExperimentCreation() {
  const batchLoadWizardContext = useContext(BatchLoadExperimentWizardContext);

  const [showData, setShowData] = useState(false);
  const [experimentCount, setExperimentCount] = useState(0);
  const [experimentPlateCount, setExperimentPlateCount] = useState(0);

  const expNameFormat = batchLoadWizardContext.expNameFormatString;

  const expNameFormatVals = expNameFormat.split('>');
  const [tableData, setTableData] = useState<BatchLoadExperimentsData[]>([]);

  const [firstCheckboxValue, setFirstCheckboxValue] = useState(
    getReviewExpEnumVal(!isNullOrUndefined(expNameFormatVals) && expNameFormatVals[0]) ||
      BatchloadExperimentReviewExpOptions.SCREENNAME,
  );

  const [secondCheckboxValue, setSecondCheckboxValue] = useState(
    getReviewExpEnumVal(!isNullOrUndefined(expNameFormatVals) && expNameFormatVals[1]) ||
      BatchloadExperimentReviewExpOptions.DATE,
  );

  const [thirdCheckboxValue, setThirdCheckboxValue] = useState(
    getReviewExpEnumVal(!isNullOrUndefined(expNameFormatVals) && expNameFormatVals[2]) ||
      BatchloadExperimentReviewExpOptions.TEMPERATURE,
  );

  const [isSecondCheckboxChecked, setIsSecondCheckboxChecked] = useState(
    2 == expNameFormatVals.length - 1
      ? true
      : expNameFormatVals.length - 1 > 2
      ? true
      : false,
  );

  const [isThirdCheckboxChecked, setIsThirdCheckboxChecked] = useState(
    3 == expNameFormatVals.length - 1,
  );

  const dateToday = formatDateWithParts(new Date(), shortDayMonthYearFormat, '-');
  const { experimentData } = batchLoadWizardContext;

  function buildExperimentNames(data: BatchLoadExperimentsData[]) {
    let lastTemplateFormat = '';
    data.forEach((row) => {
      const { expName, templateFormat } = getExperimentName(
        row.screen.value,
        row.temperature.value,
        row.plateBarcode.value,
        row.experimentNameTag?.value ?? '',
        firstCheckboxValue,
        secondCheckboxValue,
        thirdCheckboxValue,
        isSecondCheckboxChecked,
        isThirdCheckboxChecked,
        dateToday,
      );
      row.experimentName.value = expName;
      lastTemplateFormat = templateFormat;
    });
    batchLoadWizardContext.setExpNameFormatString(lastTemplateFormat);
  }

  useEffect(() => {
    const tempPreviewDataTable = groupExperimentsByPlate(experimentData);
    buildExperimentNames(tempPreviewDataTable);

    const count = tempPreviewDataTable.length;
    setExperimentCount(count);
    setExperimentPlateCount(count);
    batchLoadWizardContext.setExperimentData(tempPreviewDataTable);
    setTableData(tempPreviewDataTable);
  }, []);

  const hasDuplicateProteins = useMemo<boolean>(() => {
    const seen = new Map<string, BatchLoadDrop>(); // Key = sample, Value = Ingredients
    for (const row of batchLoadWizardContext.experimentData) {
      if (
        row.sample?.validation?.result === EValidationResult.Warning &&
        row.sample?.validation?.statusCode === ESampleValidationStatusCode.DUPLICATE
      ) {
        return true;
      }

      if (!row.drops) {
        continue;
      }
      for (const drop of row.drops) {
        if (!drop.sample?.value) {
          continue;
        }
        const key = GetNormalizedBatchloadEntryValue(drop.sample);

        // New sample instance
        if (!seen.has(key)) {
          seen.set(key, drop);
          continue;
        }

        if (!isSameDropIngredients(seen.get(key)!, drop)) {
          return true;
        }
      }
    }
    return false;
  }, [batchLoadWizardContext.experimentData]);

  useEffect(() => {
    if (showData) {
      buildExperimentNames(experimentData);
      setTableData(experimentData);
    } else {
      setShowData(true);
    }
  }, [
    firstCheckboxValue,
    secondCheckboxValue,
    thirdCheckboxValue,
    isSecondCheckboxChecked,
    isThirdCheckboxChecked,
  ]);

  function changeFirstCheckboxValue(e) {
    const numVal = parseInt(e.target.value);
    setFirstCheckboxValue(numVal);
  }
  function changeSecondCheckboxValue(e) {
    const numVal = parseInt(e.target.value);
    setSecondCheckboxValue(numVal);
  }
  function changeThirdCheckboxValue(e) {
    const numVal = parseInt(e.target.value);
    setThirdCheckboxValue(numVal);
  }

  function secondCheckboxToggle(e) {
    const flValue = e.target.checked;
    setIsSecondCheckboxChecked(flValue);
  }
  function thirdCheckboxToggle(e) {
    const flValue = e.target.checked;
    setIsThirdCheckboxChecked(flValue);
  }

  return (
    <div
      id="previewdatabeforeexperimentcreation-wrapper"
      className="flex flex-col h-[60vh] gap-2"
    >
      {showData ? (
        <>
          <div id="previewdatabeforeexperimentcreation-message" className="flex flex-col">
            <div
              id="previewdatabeforeexperimentcreation-message-text"
              style={{ fontSize: 12 }}
            >
              {' '}
              *Data has been validated. Please select options from below dropdown lists to
              format the experiment name.
            </div>
          </div>
          <div
            id="previewdatabeforeexperimentcreation-experimentnameformat-wrapper"
            className="flex flex-row w-[70%] items-center"
          >
            <span
              id="previewdatabeforeexperimentcreation-experimentnameformat-label"
              style={{ float: 'left', fontWeight: 'bold' }}
            >
              Experiment Name Format
            </span>{' '}
            <span
              id="previewdatabeforeexperimentcreation-experimentnameformat-component"
              style={{ float: 'left' }}
            >
              <BatchLoadExperimentNameReview
                tableData={tableData}
                firstCheckboxValue={firstCheckboxValue}
                secondCheckboxValue={secondCheckboxValue}
                thirdCheckboxValue={thirdCheckboxValue}
                isSecondCheckboxChecked={isSecondCheckboxChecked}
                isThirdCheckboxChecked={isThirdCheckboxChecked}
                changeFirstCheckboxValue={changeFirstCheckboxValue}
                changeSecondCheckboxValue={changeSecondCheckboxValue}
                changeThirdCheckboxValue={changeThirdCheckboxValue}
                secondCheckboxToggle={secondCheckboxToggle}
                thirdCheckboxToggle={thirdCheckboxToggle}
              />
            </span>
          </div>
          {hasDuplicateProteins && (
            <FmlxBanner
              id="previewdatabeforeexperimentcreation-duplicatedproteins-banner"
              variant={FmlxBanner.Variant.INFO}
              title={
                <span
                  id="previewdatabeforeexperimentcreation-duplicatedproteins-banner-text"
                  className={'text-[0.8rem]'}
                >
                  Any duplicate entries in the CSV file or the ROCK MAKER database will be
                  assigned a unique name tag by the Batchload Experiment Wizard.
                </span>
              }
              size={FmlxBanner.Size.SMALL}
              hideCloseButton
            />
          )}
          <div
            id="previewdatabeforeexperimentcreation-previewbatchloaddatatable-wrapper"
            className={`flex ${
              hasDuplicateProteins ? 'h[calc(48vh-64px)]' : '48vh'
            } overflow-hidden`}
          >
            <PreviewBatchloadDataTable
              tableData={tableData}
              isShowingBanner={hasDuplicateProteins}
            />
          </div>
          <div
            id="previewdatabeforeexperimentcreation-experimentcount-summary-wrapper"
            className="flex flex-row w-2/5 gap-2 mt-2"
          >
            <div id="importcsvfile-div-expcount" className="flex grow">
              Exp. Count: {experimentCount}
            </div>
            <div id="importcsvfile-div-expplatecount" className="flex grow">
              Exp. Plate Count: {experimentPlateCount}
            </div>
          </div>
        </>
      ) : (
        <div
          id="previewdatabeforeexperimentcreation-loading-wrapper"
          className="flex flex-col h-full w-full justify-center items-center gap-4"
        >
          <FmlxProgressBar
            id="previewdatabeforeexperimentcreation-progressbar"
            variant={FmlxProgressBar.Variant.INDETERMINATE}
            size={FmlxProgressBar.Size.MEDIUM}
          />
          <div
            id="previewdatabeforeexperimentcreation-loading-text"
            className="font-bold"
          >
            Loading Review Table...
          </div>
          <div
            id="previewdatabeforeexperimentcreation-loading-batchloadwizardcontext-wrapper"
            className="flex flex-row w-1/4 justify-between items-center gap-2"
          >
            <div
              id="previewdatabeforeexperimentcreation-filename"
              className="w-3/4 text-sm"
            >
              {batchLoadWizardContext.fileName}
            </div>
            <div
              id="previewdatabeforeexperimentcreation-filesize"
              className="w-1/4 text-right text-sm text-gray-400 no-wrap"
            >
              {batchLoadWizardContext.fileSize}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
